import { Form, message, Select } from "antd";
import {
  isPermissionNotGrantted,
  ProjectContext,
  TProjectContext
} from "context/ProjectProvider";
import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { AcceptanceStatus, ProjectPermissionEnum } from "constants/index";
import dlTagRenderer from "components/dl-tag-renderer/dl-tag-renderer";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import { QUERY_ACTIVE_PROJECT_USERS } from "services/graphQL/queries";
import {
  FEATURE_DISTRIBUTION_GROUP_LIST,
  FEATURE_DISTRIBUTION_GROUP_USERS
} from "entity-app/graphQL/ciq-feature-queries";
import {
  INSERT_FEATURE_DISTRIBUTION_LIST,
  UPDATE_FEATURE_DISTRIBUTION_LIST
} from "entity-app/graphQL/ciq-feature-mutations";

function FeatureDistributionList(props: {
  featureId: string;
  disabled: boolean;
}) {
  const { featureId, disabled } = props;
  const { projectId } = useParams() as any;

  const { gqlClientForProject, tokenContents, eventLogs }: TProjectContext =
    useContext(ProjectContext);

  const { data: usersData, refetch: refetchProjectUsersData } = useCIQQuery(
    QUERY_ACTIVE_PROJECT_USERS,
    {
      client: gqlClientForProject,
      variables: {
        where: {
          project_id: { _eq: projectId },
          status_id: { _eq: AcceptanceStatus.ACTIVE }
        }
      },
      skip: !gqlClientForProject || !projectId
    }
  );

  const [insertDLToFeature] = useCIQMutation(INSERT_FEATURE_DISTRIBUTION_LIST, {
    client: gqlClientForProject
  });

  const [updateDLToFeature] = useCIQMutation(UPDATE_FEATURE_DISTRIBUTION_LIST, {
    client: gqlClientForProject
  });

  const {
    data: featuredistributionGroupData,
    refetch: refetchfeaturedistributionGroupData
  }: { data: any; refetch: any } = useCIQQuery(
    FEATURE_DISTRIBUTION_GROUP_LIST,
    {
      client: gqlClientForProject,
      skip: !gqlClientForProject,
      variables: {
        where: {
          project_id: {
            _eq: projectId
          }
        }
      }
    }
  );

  const {
    data: dldata,
    refetch: refetchfeaturedldata
  }: { data: any; refetch: any } = useCIQQuery(
    FEATURE_DISTRIBUTION_GROUP_USERS,
    {
      client: gqlClientForProject,
      skip: !gqlClientForProject,
      variables: {
        where: {
          feature_instance_id: {
            _eq: featureId
          }
        }
      }
    }
  );

  const [selectedDLIds, setselectedGroupIds] = useState<any>([]);

  const [options, setOptions] = useState<any>([]);

  useEffect(() => {
    const tempProjectUsers: any = [];
    const selectedgroupIds: any = [];
    const selecteduserIds: any = [];

    dldata?.feature_distribution_list?.forEach((element: any) => {
      if (element.dl_id) {
        selectedgroupIds.push(element.dl_id);
      }
      if (element.user_id) {
        selecteduserIds.push(element.user_id);
      }

      if (featuredistributionGroupData?.feature_distribution_list) {
        if (element.dl_id) {
          const groups: any =
            featuredistributionGroupData?.feature_distribution_list?.filter(
              (grp: any) => grp?.dl_id === element.dl_id
            );
          if (groups?.length > 0) {
            groups[0]?.distribution_group?.distribution_group_user?.forEach(
              (user: any) => {
                tempProjectUsers.push(user?.user?.id);
              }
            );
          }
        }
      }
    });

    const filterSelectedUserIds = selecteduserIds.filter((usr_id: any) => {
      return !tempProjectUsers.find((userid: any) => {
        return usr_id === userid;
      });
    });
    const filterUserArray: [] = usersData?.project_users?.filter(
      (oldItem: any) => {
        return !tempProjectUsers.find((userid: any) => {
          return oldItem?.user?.id === userid;
        });
      }
    );

    const userArr: any = [];

    filterUserArray?.forEach((item: any) => {
      if (item?.status_id === AcceptanceStatus.ACTIVE) {
        userArr.push({
          label: `${item?.user?.first_name} ${item?.user?.last_name}`,
          value: item?.user?.id,
          email: item?.user?.email,
          status_id: item?.status_id
        });
      }
    });

    const grpArr: any = [];

    featuredistributionGroupData?.feature_distribution_list
      ?.filter((group: any) => group?.dl_id)
      ?.forEach((item: any) => {
        grpArr.push({
          label: item?.distribution_group?.name,
          value: item?.distribution_group?.id,
          users: item?.distribution_group?.distribution_group_user
        });
      });

    const grpOption = { label: "Groups", options: grpArr };
    const userOption = { label: "Users", options: userArr };
    setOptions([grpOption, userOption]);
    setselectedGroupIds([...selectedgroupIds, ...filterSelectedUserIds]);
  }, [
    featuredistributionGroupData,
    dldata?.feature_distribution_list,
    usersData?.project_users
  ]);

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs.length && previousEventLogs.current !== eventLogs) {
      if (eventLogs.some((log) => log.data_source === "distribution_group")) {
        refetchfeaturedistributionGroupData();
      }

      if (eventLogs.some((log) => log.data_source === "project_users")) {
        refetchProjectUsersData();
      }

      if (
        eventLogs.some(
          (log) =>
            log.data_source === "feature_distribution_list" &&
            log.info.feature_instance_ids?.includes(featureId)
        )
      ) {
        refetchfeaturedldata();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [
    eventLogs,
    refetchProjectUsersData,
    refetchfeaturedistributionGroupData,
    featureId,
    refetchfeaturedldata
  ]);

  async function onDeSelect(deselectedId: any, isGroupId: boolean) {
    const deleteArr: any = [];
    if (isGroupId) {
      deleteArr.push({
        dl_id: [deselectedId],
        user_id: []
      });
    } else {
      deleteArr.push({
        dl_id: [],
        user_id: [deselectedId]
      });
    }

    if (deleteArr?.length > 0) {
      const updateDLToSubmittalResponse = await updateDLToFeature({
        variables: {
          where: {
            _or: [
              { dl_id: { _in: deleteArr[0].dl_id } },
              { user_id: { _in: deleteArr[0].user_id } }
            ],
            feature_instance_id: { _eq: featureId }
          }
        }
      });

      if (updateDLToSubmittalResponse.data) {
        message.success("Distribution list updated.");
      }

      if (updateDLToSubmittalResponse.errors) {
        message.error("Failed to update distribution list.");
      }
    }
  }

  async function onSelect(selctedId: any, isGroupId: boolean) {
    const addArr: any = [];
    if (isGroupId) {
      addArr.push({
        dl_id: selctedId,
        user_id: null,
        feature_instance_id: featureId
      });
    } else {
      addArr.push({
        dl_id: null,
        user_id: selctedId,
        feature_instance_id: featureId
      });
    }
    if (addArr.length > 0) {
      const insertDLToSubmittalResponse = await insertDLToFeature({
        variables: {
          data: addArr,
          constraint: isGroupId
            ? "feature_distribution_list_feature_instance_id_dl_id_key"
            : "feature_distribution_list_feature_instance_id_user_id_key"
        }
      });
      if (
        insertDLToSubmittalResponse?.data?.insert_feature_distribution_list
          ?.affected_rows > 0
      ) {
        message.success("Distribution list updated.");
      }

      if (insertDLToSubmittalResponse.errors) {
        message.error("Failed to update distribution list.");
      }
    }
  }

  return (
    <Form.Item
      label="Distribution list"
      className={`distribution-list-input ${
        !selectedDLIds || !selectedDLIds.length ? "hide-input" : ""
      }`}
    >
      <Select
        disabled={
          disabled ||
          isPermissionNotGrantted(
            ProjectPermissionEnum.EditDistributionGroup,
            tokenContents?.role!
          )
        }
        placeholder="Select distribution groups"
        data-testid="featuredistributionlist-select"
        style={{ width: "100%" }}
        virtual={false}
        options={options}
        mode="multiple"
        value={selectedDLIds}
        filterOption
        optionFilterProp="label"
        onChange={(groupIds: any) => {
          setselectedGroupIds(groupIds);
        }}
        onSelect={(e: any) => {
          const arr: [] =
            featuredistributionGroupData?.feature_distribution_list?.filter(
              (group: any) => group.dl_id && e === group?.dl_id
            );
          if (arr.length > 0) {
            onSelect(e, true);
          } else {
            onSelect(e, false);
          }
        }}
        onDeselect={(e: any) => {
          const arr: [] =
            featuredistributionGroupData?.feature_distribution_list?.filter(
              (group: any) => group.dl_id && e === group?.dl_id
            );

          if (arr.length > 0) {
            onDeSelect(e, true);
          } else {
            onDeSelect(e, false);
          }
        }}
        tagRender={(prop) =>
          dlTagRenderer(
            prop,
            usersData?.project_users ?? [],
            options.length > 0 ? options[0]?.options : null
          )
        }
      />
    </Form.Item>
  );
}

export default FeatureDistributionList;
